import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { HeartPulse, Package, HandHeart, Search, RefreshCw, AlertTriangle, ChevronDown, ChevronRight, ArrowLeftRight } from 'lucide-react';
import { EditLotExpiration } from '../components/EditLotExpiration';
import { toast } from 'sonner';
import { fmtDate as fmtDateTz } from '../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (d) => (d ? fmtDateTz(d) : '-');

const daysUntil = (iso) => {
  if (!iso) return null;
  try {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const exp = new Date(iso); exp.setHours(0, 0, 0, 0);
    return Math.round((exp - today) / 86400000);
  } catch { return null; }
};

function NursingStock() {
  const { user } = useOutletContext();
  const [areas, setAreas] = useState([]);
  const [selectedAreaId, setSelectedAreaId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const isAdmin = user?.role === 'admin';
  const toggleExpand = (id) => setExpandedId(prev => prev === id ? null : id);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/me/areas`, { withCredentials: true });
        const list = (r.data || []).filter(a => a.destination_warehouse_id);
        setAreas(list);
        if (list.length === 1) setSelectedAreaId(list[0].area_id);
      } catch (e) {
        toast.error('No se pudieron cargar las áreas');
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedAreaId) { setData(null); return; }
    const area = areas.find(a => a.area_id === selectedAreaId);
    if (!area?.destination_warehouse_id) return;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/warehouses/${area.destination_warehouse_id}/stock`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => toast.error('Error al cargar stock del área'))
      .finally(() => setLoading(false));
  }, [selectedAreaId, areas, refreshKey]);

  const filtered = useMemo(() => {
    if (!data?.items) return [];
    if (!search.trim()) return data.items;
    const s = search.toLowerCase();
    return data.items.filter(it =>
      (it.name || '').toLowerCase().includes(s) ||
      (it.category || '').toLowerCase().includes(s)
    );
  }, [data, search]);

  const totalUnits = useMemo(
    () => (data?.items || []).reduce((acc, it) => acc + (it.stock || 0), 0),
    [data]
  );

  const selectedArea = areas.find(a => a.area_id === selectedAreaId);

  return (
    <div className="space-y-6" data-testid="nursing-stock-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <HeartPulse className="w-7 h-7 text-rose-600" /> Stock del Área
          </h1>
          <p className="text-muted-foreground mt-1">
            Subdepósito de tu/s área/s de Enfermería. Lo que tenés disponible para dispensar a residentes.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => selectedAreaId && setSelectedAreaId(selectedAreaId)} disabled={!selectedAreaId}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refrescar
        </Button>
      </div>

      {/* Selector de área */}
      {areas.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <HeartPulse className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>No estás asignado a ninguna área de Enfermería con subdepósito configurado.</p>
            <p className="text-xs mt-2">Pedile a un administrador que te asigne a un área.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {areas.length > 1 && (
            <Card>
              <CardContent className="py-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                  <div className="sm:col-span-2">
                    <Label>Seleccionar área</Label>
                    <select
                      value={selectedAreaId}
                      onChange={(e) => setSelectedAreaId(e.target.value)}
                      className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                      data-testid="area-selector"
                    >
                      <option value="">(Elegí un área)</option>
                      {areas.map(a => (
                        <option key={a.area_id} value={a.area_id}>{a.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {selectedAreaId && data && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2"><CardDescription>Área</CardDescription></CardHeader>
                  <CardContent><p className="text-xl font-bold">{selectedArea?.name}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardDescription>Artículos</CardDescription></CardHeader>
                  <CardContent><p className="text-3xl font-bold">{data?.items?.length || 0}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardDescription>Unidades totales</CardDescription></CardHeader>
                  <CardContent><p className="text-3xl font-bold">{totalUnits.toLocaleString()}</p></CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardDescription>Próx. vencimientos (30d)</CardDescription></CardHeader>
                  <CardContent>
                    <p className="text-3xl font-bold text-amber-600">
                      {(data?.summary?.expiring_30) || 0}
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Detalle</CardTitle>
                  <CardDescription>
                    Los lotes marcados con 🔒 son medicación traída por familiares; uso exclusivo del residente indicado.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-3 relative">
                    <Search className="absolute left-2 top-2.5 w-4 h-4 text-muted-foreground" />
                    <Input
                      className="pl-8"
                      placeholder="Buscar artículo o categoría..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                  </div>
                  {loading ? (
                    <div className="py-8 text-center text-muted-foreground">Cargando...</div>
                  ) : filtered.length === 0 ? (
                    <div className="py-8 text-center text-muted-foreground" data-testid="empty-state">
                      Sin stock en este subdepósito.
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8"></TableHead>
                          <TableHead>Artículo</TableHead>
                          <TableHead>Categoría</TableHead>
                          <TableHead className="text-right">Stock</TableHead>
                          <TableHead className="text-right">Lotes</TableHead>
                          <TableHead>Próx. Vto.</TableHead>
                          <TableHead>Notas</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.map((it) => {
                          const d = it.days_to_expiration;
                          const expSoon = d !== null && d >= 0 && d <= 30;
                          const expired = d !== null && d < 0;
                          const hasExternal = (it.lots || []).some(l => l.from_external_reception);
                          const hasAreaTransfer = (it.lots || []).some(l => l.from_area_transfer);
                          const isExpanded = expandedId === it.article_id;
                          const hasLots = (it.lots || []).length > 0;
                          return (
                            <React.Fragment key={it.article_id}>
                              <TableRow
                                data-testid={`row-${it.article_id}`}
                                className={hasLots ? 'cursor-pointer hover:bg-accent' : ''}
                                onClick={() => hasLots && toggleExpand(it.article_id)}
                              >
                                <TableCell>
                                  {hasLots && (isExpanded
                                    ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                    : <ChevronRight className="w-4 h-4 text-muted-foreground" />)}
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    {it.is_medicamento && <span title="Medicamento">💊</span>}
                                    <Package className="w-3 h-3 text-muted-foreground" />
                                    <span className="font-medium">{it.name}</span>
                                    {it.is_archived && (
                                      <span className="text-xs font-semibold text-amber-600" data-testid={`archived-label-${it.article_id}`}>(SIN REPOSICION-UTILIZAR HASTA AGOTAR)</span>
                                    )}
                                    {hasExternal && (
                                      <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-300 text-xs" title="Tiene lotes de origen REM (familiar)">
                                        <HandHeart className="w-3 h-3 mr-1" /> REM
                                      </Badge>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {it.category ? <Badge variant="outline">{it.category}</Badge> : <span className="text-muted-foreground">-</span>}
                                </TableCell>
                                <TableCell className="text-right font-mono">{it.stock} <span className="text-xs text-muted-foreground">{it.unit || 'unidad'}</span></TableCell>
                                <TableCell className="text-right">{it.lots_count || (it.lots || []).length}</TableCell>
                                <TableCell>
                                  <span className={expired ? 'text-red-600 font-semibold' : expSoon ? 'text-amber-600 font-semibold' : ''}>
                                    {fmtDate(it.next_expiration)}
                                    {expired && ' ⚠️'}
                                    {expSoon && !expired && ` (${d}d)`}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  {(hasExternal || hasAreaTransfer) && (
                                    <div className="text-xs space-y-1">
                                      {(it.lots || []).filter(l => l.from_external_reception).slice(0, 2).map(l => (
                                        <div key={l.lot_id} className="flex items-center gap-1 text-rose-700">
                                          <HandHeart className="w-3 h-3" />
                                          <span title={`Origen: ${l.from_external_reception}`}>
                                            REM · <strong>{l.rem_patient_name || '—'}</strong>
                                          </span>
                                        </div>
                                      ))}
                                      {(it.lots || []).filter(l => l.from_area_transfer).slice(0, 2).map(l => (
                                        <div key={l.lot_id} className="flex items-center gap-1 text-blue-700">
                                          <ArrowLeftRight className="w-3 h-3" />
                                          <span title={`Transferencia ${l.from_area_transfer}`}>
                                            Área · <strong>{l.origin_area_name || '—'}</strong>
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </TableCell>
                              </TableRow>
                              {isExpanded && hasLots && (
                                <TableRow className="bg-muted/30 hover:bg-muted/30" data-testid={`lots-detail-${it.article_id}`}>
                                  <TableCell colSpan={7} className="py-2">
                                    <NursingLotsDetail lots={it.lots} isAdmin={isAdmin} onSaved={() => setRefreshKey(k => k + 1)} />
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {selectedAreaId === '' && areas.length > 1 && (
            <Card>
              <CardContent className="py-10 text-center text-muted-foreground">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-40" />
                Seleccioná un área para ver su subdepósito.
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

export default NursingStock;

function NursingLotsDetail({ lots, isAdmin, onSaved }) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const sorted = [...lots].sort((a, b) => {
    const da = a.expiration_date ? new Date(a.expiration_date).getTime() : Infinity;
    const dbb = b.expiration_date ? new Date(b.expiration_date).getTime() : Infinity;
    return da - dbb;
  });
  return (
    <div className="pl-8 pr-4">
      <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">Lotes que componen el stock</div>
      <div className="grid grid-cols-12 text-[11px] font-semibold text-muted-foreground border-b pb-1 mb-1 uppercase gap-x-4">
        <span className="col-span-3">Lote</span>
        <span className="col-span-2 text-right">Cantidad</span>
        <span className="col-span-3">Vencimiento</span>
        <span className="col-span-2 text-right">Días</span>
        <span className="col-span-2">Origen</span>
      </div>
      {sorted.map(l => {
        const exp = l.expiration_date ? new Date(l.expiration_date) : null;
        if (exp) exp.setHours(0, 0, 0, 0);
        const days = exp ? Math.round((exp - today) / 86400000) : null;
        const tone = days === null ? '' : (days < 0 ? 'text-red-600 font-semibold' : days <= 30 ? 'text-amber-600 font-medium' : days <= 90 ? 'text-amber-500' : '');
        return (
          <div key={l.lot_id} className="grid grid-cols-12 text-xs py-1 border-b last:border-0 gap-x-4">
            <span className="col-span-3 font-mono">{l.lot_number || '—'}</span>
            <span className="col-span-2 text-right tabular-nums font-semibold">{l.quantity_remaining}</span>
            <span className={`col-span-3 flex items-center gap-1 ${tone}`}>
              {fmtDate(l.expiration_date)}
              {isAdmin && <EditLotExpiration lot={l} onSaved={onSaved} />}
            </span>
            <span className={`col-span-2 text-right tabular-nums ${tone}`}>
              {days === null ? '—' : days < 0 ? `vencido hace ${-days}d` : `${days}d`}
            </span>
            <span className="col-span-2">
              {l.from_external_reception
                ? <span className="inline-flex items-center gap-1 text-rose-700" title={`Origen: ${l.from_external_reception}`}><HandHeart className="w-3 h-3" />REM · {l.rem_patient_name || '—'}</span>
                : l.from_area_transfer
                ? <span className="inline-flex items-center gap-1 text-blue-700" title={`Transferencia ${l.from_area_transfer}`}><ArrowLeftRight className="w-3 h-3" />Área · {l.origin_area_name || '—'}</span>
                : <span className="text-muted-foreground">Compras</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
}
